import { useState } from "react";

// Shared install content for the omni-hub-cli, rendered in two places: the Agents
// Hub overview panel (CliDownloadPanel) and the top-bar popover (CliInstallButton).
// The hub serves the CLI as a single dependency-free bundle at /cli/omni-hub-cli.mjs
// plus generated installers at /cli/install.sh|ps1 (see the api's cli-download.ts).
// The one-liners fetch those scripts from THIS origin, so they always point at the
// hub the user is looking at — nothing hardcoded.

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button className="cli-copy" onClick={copy} title="Copy to clipboard">
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function CommandRow({ label, command }: { label: string; command: string }) {
  return (
    <div className="cli-cmd">
      <span className="cli-cmd-label">{label}</span>
      <div className="cli-cmd-line">
        <code>{command}</code>
        <CopyButton text={command} />
      </div>
    </div>
  );
}

export function CliInstallContent() {
  // Same-origin: the SPA is served by the api, so location.origin IS the hub.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shCmd = `curl -fsSL ${origin}/cli/install.sh | sh`;
  const ps1Cmd = `irm ${origin}/cli/install.ps1 | iex`;
  const mcpConfig = `{
  "mcpServers": {
    "omni-hub": {
      "command": "omni-hub-cli",
      "args": ["mcp"],
      "env": {
        "OMNI_HUB_URL": "${origin}",
        "OMNI_HUB_AGENT_CONFIG": "/Users/you/.omni-hub/hub-agent.json"
      }
    }
  }
}`;

  return (
    <>
      <div className="cli-panel-head">
        <h2 className="cli-panel-title">Use it from your desktop</h2>
        <a className="cli-download-link" href={`${origin}/cli/omni-hub-cli.mjs`} download>
          ↓ omni-hub-cli.mjs
        </a>
      </div>
      <p className="cli-panel-sub">
        Install the <code>omni-hub-cli</code> — the command that connects Claude Desktop (and any MCP
        client) to this hub. One line; requires <strong>Node.js 20+</strong>.
      </p>

      <CommandRow label="macOS / Linux" command={shCmd} />
      <CommandRow label="Windows (PowerShell)" command={ps1Cmd} />

      <details className="cli-more">
        <summary>Connect Claude Desktop</summary>
        <p className="cli-more-note">
          After installing, sign in once, then add the hub to <code>claude_desktop_config.json</code> (macOS:{" "}
          <code>~/Library/Application Support/Claude/</code>):
        </p>
        <div className="cli-cmd-line">
          <code>omni-hub-cli login</code>
          <CopyButton text="omni-hub-cli login" />
        </div>
        <div className="cli-code-block">
          <pre>{mcpConfig}</pre>
          <CopyButton text={mcpConfig} />
        </div>
        <p className="cli-more-note">Restart Claude Desktop; the <code>omni_hub_*</code> tools then appear.</p>
      </details>
    </>
  );
}
